import { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Collapse,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { ErrorState } from '../../../../shared/components/ErrorState';
import { EmptyState } from '../../../../shared/components/EmptyState';
import { describeApiError } from '../../../../shared/api/errors';
import { useAuth } from '../../../../shared/auth/useAuth';
import { useEmployeeValesHooks } from '../hooks';
import type { EmployeeValeListItem, EmployeeValeParcelaDto, ValeStatus } from '../types';

function formatDate(iso: string): string {
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD/MM/YYYY') : iso;
}

function formatBRL(value: number | null | undefined): string {
  return (value ?? 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  });
}

function statusChipColor(status: ValeStatus): 'default' | 'success' | 'warning' {
  if (status === 'PAG') return 'success';
  if (status === 'CAN') return 'default';
  return 'warning';
}

function ValeCard({
  vale,
  expanded,
  onToggle,
  parcelas,
  parcelasLoading,
  parcelasError,
}: {
  vale: EmployeeValeListItem;
  expanded: boolean;
  onToggle: () => void;
  parcelas: EmployeeValeParcelaDto[] | undefined;
  parcelasLoading: boolean;
  parcelasError: string | null;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <Card variant="outlined" sx={{ mb: 1.5 }}>
      <CardActionArea
        onClick={onToggle}
        aria-expanded={expanded}
        aria-label={t('features.colaboradores.vales.meus.toggle', { id: vale.id })}
        sx={{ minHeight: 56 }}
      >
        <CardContent sx={{ py: 1.25, px: 1.5, '&:last-child': { pb: 1.25 } }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" color="text.secondary">
                {t('features.colaboradores.vales.meus.solicitadoEm', {
                  data: formatDate(vale.dataSolicitacao),
                })}
              </Typography>
              <Typography
                component="div"
                sx={{ fontFamily: 'monospace', fontSize: 22, fontWeight: 600 }}
              >
                {formatBRL(vale.valor)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('features.colaboradores.vales.meus.parcelasInfo', {
                  count: vale.nroParcelas,
                  primeira: formatDate(vale.vencimento1aParcela),
                })}
              </Typography>
            </Box>
            <Stack alignItems="flex-end" spacing={0.5}>
              <Chip
                size="small"
                label={t(`features.colaboradores.vales.status.${vale.status}`)}
                color={statusChipColor(vale.status)}
                variant={vale.status === 'ABE' ? 'filled' : 'outlined'}
              />
              <ExpandMoreIcon
                sx={{
                  transform: expanded ? 'rotate(180deg)' : 'none',
                  transition: '150ms',
                  color: 'text.secondary',
                }}
              />
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>
      <Collapse in={expanded} unmountOnExit>
        <Divider />
        <Box sx={{ px: 1.5, py: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {t('features.colaboradores.vales.meus.parcelasTitle')}
          </Typography>
          {parcelasLoading && <Skeleton variant="rectangular" height={64} />}
          {parcelasError && (
            <Typography variant="body2" color="error">
              {parcelasError}
            </Typography>
          )}
          {!parcelasLoading && !parcelasError && parcelas && parcelas.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {t('features.colaboradores.vales.meus.semParcelas')}
            </Typography>
          )}
          {!parcelasLoading && !parcelasError && parcelas && parcelas.length > 0 && (
            <Stack divider={<Divider />} spacing={0}>
              {parcelas.map((p) => (
                <Stack key={p.id} direction="row" alignItems="center" spacing={1} sx={{ py: 0.75 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2">
                      {t('features.colaboradores.vales.meus.parcelaNumero', { n: p.id })}
                      {' · '}
                      {formatDate(p.dataVencimento)}
                    </Typography>
                    {p.dataPagamento && (
                      <Typography variant="caption" color="text.secondary">
                        {t('features.colaboradores.vales.meus.pagoEm', {
                          data: formatDate(p.dataPagamento),
                          valor: formatBRL(p.valorPago ?? p.valor),
                        })}
                      </Typography>
                    )}
                  </Box>
                  <Typography sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                    {formatBRL(p.valor)}
                  </Typography>
                  <Chip
                    size="small"
                    label={t(`features.colaboradores.vales.status.${p.status}`)}
                    color={statusChipColor(p.status)}
                    variant={p.status === 'ABE' ? 'filled' : 'outlined'}
                  />
                </Stack>
              ))}
            </Stack>
          )}
        </Box>
      </Collapse>
    </Card>
  );
}

function GroupedVales({
  vales,
  expandedId,
  onToggle,
  employeeId,
}: {
  vales: EmployeeValeListItem[];
  expandedId: number | null;
  onToggle: (id: number) => void;
  employeeId: number;
}): JSX.Element {
  const { t } = useTranslation();
  const hooks = useEmployeeValesHooks(employeeId);
  const parcelasQuery = hooks.useParcelas(expandedId);

  const buckets = useMemo(() => {
    const order: ValeStatus[] = ['ABE', 'PAG', 'CAN'];
    const map = new Map<ValeStatus, EmployeeValeListItem[]>();
    for (const s of order) map.set(s, []);
    for (const v of vales) (map.get(v.status) ?? []).push(v);
    return order.map((s) => ({ status: s, items: map.get(s) ?? [] })).filter((b) => b.items.length);
  }, [vales]);

  return (
    <>
      {buckets.map((bucket) => (
        <Box key={bucket.status} sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.5, px: 0.5 }}>
            <Typography variant="subtitle2">
              {t(`features.colaboradores.vales.status.${bucket.status}`)}
            </Typography>
            <Box sx={{ flex: 1 }}>
              <Divider />
            </Box>
            <Typography variant="caption" color="text.secondary">
              {bucket.items.length}
            </Typography>
          </Stack>
          {bucket.items.map((v) => (
            <ValeCard
              key={v.id}
              vale={v}
              expanded={expandedId === v.id}
              onToggle={() => onToggle(v.id)}
              parcelas={expandedId === v.id ? parcelasQuery.data?.data : undefined}
              parcelasLoading={expandedId === v.id && parcelasQuery.isLoading}
              parcelasError={
                expandedId === v.id && parcelasQuery.isError
                  ? describeApiError(parcelasQuery.error)
                  : null
              }
            />
          ))}
        </Box>
      ))}
    </>
  );
}

const VALE_STATUS_ORDER: ValeStatus[] = ['ABE', 'PAG', 'CAN'];

export function MeusVales(): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();
  const employeeId = user?.externalId ?? null;
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [statusFiltro, setStatusFiltro] = useState<ValeStatus | 'all'>('all');

  // hooks só fazem sentido quando o user tem externalId — bloqueamos render senão.
  const hooks = useEmployeeValesHooks(employeeId ?? 0);
  const listQuery = hooks.useList({
    page: 1,
    pageSize: 100,
    sortBy: 'dataSolicitacao',
    sortOrder: 'desc',
  });

  if (employeeId === null) {
    return (
      <ErrorState
        description={t('features.colaboradores.vales.meus.noEmployeeId')}
        onRetry={() => undefined}
      />
    );
  }

  if (listQuery.isLoadingError) {
    return (
      <ErrorState
        description={describeApiError(listQuery.error)}
        onRetry={() => listQuery.refetch()}
      />
    );
  }

  const rows = listQuery.data?.data ?? [];
  // KPI soma TODOS os vales em aberto, independente do filtro de status.
  const totalAberto = rows.filter((v) => v.status === 'ABE').reduce((acc, v) => acc + v.valor, 0);

  // Status presentes nos dados (na ordem canônica) pra montar a faixa de filtro.
  const statusPresentes = VALE_STATUS_ORDER.filter((s) => rows.some((v) => v.status === s));
  const visibleRows = statusFiltro === 'all' ? rows : rows.filter((v) => v.status === statusFiltro);

  return (
    <Box sx={{ pb: 2 }}>
      <Box
        sx={{
          p: 2,
          mb: 2,
          position: 'sticky',
          top: 56,
          zIndex: (theme) => theme.zIndex.appBar - 1,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="overline" color="text.secondary">
          {t('features.colaboradores.vales.meus.totalAberto')}
        </Typography>
        <Typography variant="h3" component="div" sx={{ fontFamily: 'monospace', lineHeight: 1 }}>
          {listQuery.isLoading ? '—' : formatBRL(totalAberto)}
        </Typography>

        {statusPresentes.length > 1 && (
          <Stack
            direction="row"
            spacing={1}
            sx={{ mt: 1.5, overflowX: 'auto', pb: 0.5, '::-webkit-scrollbar': { display: 'none' } }}
          >
            <Chip
              label={t('features.colaboradores.vales.meus.filtroStatusTodos')}
              size="small"
              color={statusFiltro === 'all' ? 'primary' : 'default'}
              variant={statusFiltro === 'all' ? 'filled' : 'outlined'}
              onClick={() => setStatusFiltro('all')}
            />
            {statusPresentes.map((s) => (
              <Chip
                key={s}
                label={t(`features.colaboradores.vales.status.${s}`)}
                size="small"
                color={statusFiltro === s ? 'primary' : 'default'}
                variant={statusFiltro === s ? 'filled' : 'outlined'}
                onClick={() => setStatusFiltro(s)}
              />
            ))}
          </Stack>
        )}
      </Box>

      {!listQuery.isLoading && rows.length === 0 ? (
        <EmptyState description={t('features.colaboradores.vales.meus.empty')} />
      ) : (
        <GroupedVales
          vales={visibleRows}
          expandedId={expandedId}
          onToggle={(id) => setExpandedId((curr) => (curr === id ? null : id))}
          employeeId={employeeId}
        />
      )}
    </Box>
  );
}
